import { Message } from '@components/chat/Message'
import type { MessageType } from '@components/chat/types'
import { isSystemMessage } from '@components/chat/types'
import { useGetConversation } from '@features/conversation/hooks/useGetConversation'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useParams } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  messages: Array<MessageType>
  isLoading?: boolean
}

export const MessageList = ({ messages, isLoading }: Props) => {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const { id } = useParams({ strict: false })
  const { isLoading: isLoadingMessages } = useGetConversation(id)

  useEffect(() => {
    boxRef.current?.scrollTo({ behavior: 'smooth', top: boxRef.current.scrollHeight })
  }, [messages])

  return (
    <Box
      width="100%"
      display="grid"
      justifyContent="center"
      justifyItems="center"
      overflow="auto"
      gridTemplateColumns="1fr"
      ref={boxRef}
    >
      {isLoadingMessages ? (
        <Box
          width="75%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
          gap={2}
          py={4}
        >
          <CircularProgress size={60} />
        </Box>
      ) : messages.length ? (
        <Box
          width="75%"
          display="flex"
          flexDirection="column"
          gap={2}
          py={4}
        >
          {messages.map((message, index, array) => (
            <Message
              key={index}
              createdAt={message.created_at}
              message={isSystemMessage(message) ? message.answer : message.query}
              position={isSystemMessage(message) ? 'left' : 'right'}
              typing={isLoading && index === array.length - 1}
              documents={message?.documents}
              images={isSystemMessage(message) ? message?.images : undefined}
              error={isSystemMessage(message) && !!message.error}
            />
          ))}
        </Box>
      ) : (
        <Box
          width="75%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          py={4}
        >
          <Message
            message={t('index.documents.no_message_info')}
            position={'left'}
            empty
          />
        </Box>
      )}
    </Box>
  )
}
